import { PortReader, TimeoutError } from "./SerialUtils";
import { Request, Response, WssConfig } from "./generated/wss_pb";
import { Mutex } from "async-mutex";

class PortDisconnectedError extends Error {
    constructor(m: string) {
        super(m);

        // Set the prototype explicitly.
        Object.setPrototypeOf(this, PortDisconnectedError.prototype);
    }
}

const writeRequest = async (port: SerialPort, o: Request, commandId: number) => {
    o.setCmdid(commandId);

    const bin = o.serializeBinary();

    const fullBuffer = new Uint8Array(bin.length + 1);
    fullBuffer[0] = bin.length - 1;
    fullBuffer.set(bin, 1);

    if (!port.writable) {
        throw new PortDisconnectedError('Serial port writable was null, port disconnected?');
    }

    const writer = port.writable.getWriter();
    await writer.write(fullBuffer);
    writer.releaseLock();
};

const readResponse = async (port: SerialPort, timeoutMs: number): Promise<Response | undefined> => {
    const readable = port.readable;

    if (!readable) {
        throw new PortDisconnectedError('Serial port readable was null, port disconnected?');
    }

    const reader = readable.getReader();

    try {
        const pr = new PortReader(reader);

        // Read length byte
        const lengthByte = await pr.read(1, timeoutMs);

        if (!lengthByte) {
            return undefined;
        }

        const dataBytes = await pr.read(lengthByte[0] + 1, 1000);

        if (!dataBytes) {
            return undefined;
        }

        const resp = Response.deserializeBinary(dataBytes);

        console.log(`Got response: id: ${resp.getCmdid()} type: ${describeResponse(resp)}`);

        return resp;
    } catch (e) {
        if (e instanceof PortDisconnectedError) {
            throw e;
        }
        if (e instanceof TimeoutError) {
            throw e;
        }
        throw new PortDisconnectedError(`Serial read failed: ${e}`);
    } finally {
        reader.releaseLock();
    }
};

let nextCmdId = 100;

const flushPort = async (port: SerialPort) => {
    const readable = port.readable;
    if (!readable) return;

    const reader = readable.getReader();
    try {
        // Read and discard any buffered data with a short timeout
        while (true) {
            const result = await Promise.race([
                reader.read(),
                new Promise<{ value: undefined, done: true }>(resolve =>
                    setTimeout(() => resolve({ value: undefined, done: true }), 100)
                ),
            ]);

            if (result.done || !result.value) break;
            console.log(`Flushed ${(result.value as Uint8Array).length} bytes`);
        }
    } finally {
        reader.releaseLock();
    }
};

class TransactionIdMismatchError extends Error {
    constructor(m: string) {
        super(m);
        Object.setPrototypeOf(this, TransactionIdMismatchError.prototype);
    }
}

const protobufTransactImpl = async (port: SerialPort, request: Request, timeoutMs: number = 5000): Promise<Response | undefined> => {
    const start = Date.now();

    const cmdId = nextCmdId++;

    await writeRequest(port, request, cmdId);
    const result = await readResponse(port, timeoutMs);

    const duration = Date.now() - start;
    console.log(`duration ${duration}`);

    // Check that the received command ID matches expected
    if (result && result.getCmdid() !== cmdId) {
        throw new TransactionIdMismatchError(`Transaction ID mismatch expected ${cmdId} but got ${result.getCmdid()} for command ${describeRequest(request)} and got back ${describeResponse(result)}`);
    }

    return result;
};

const mutex = new Mutex();
export const protobufTransact = async (port: SerialPort, request: Request, onDisconnect: (reason: string) => void, timeoutMs: number = 5000): Promise<Response | undefined> => {
    try {
        // await here to observe error
        return await mutex.runExclusive(() => protobufTransactImpl(port, request, timeoutMs));
    } catch (e) {
        console.error(`protobufTransact failed with error ${e}`);

        if (e instanceof TransactionIdMismatchError) {
            // Flush stale data and retry once
            console.warn('Transaction ID mismatch, flushing serial port and retrying');
            try {
                return await mutex.runExclusive(async () => {
                    await flushPort(port);
                    return protobufTransactImpl(port, request, timeoutMs);
                });
            } catch (retryErr) {
                console.error(`Retry after mismatch also failed: ${retryErr}`);
                return undefined;
            }
        } else if (e instanceof TimeoutError) {
            onDisconnect('Device not responding (timeout)');
        } else if (e instanceof PortDisconnectedError) {
            onDisconnect('Device was disconnected');
        } else {
            onDisconnect(`Communication error: ${e}`);
        }
    }
}

export const isAck = (response?: Response): boolean => {
    return !!(response && response.hasAck() && !response.hasNak());
};

export const describeConfiguration = (config: WssConfig): string => {
    return `WssConfig type ${config.getType().toString()}`
        + ` wheel dirs [ ${config.getDirlf()} ${config.getDirrf()} ${config.getDirlr()} ${config.getDirrr()} ] speedo pulse/km ${config.getSpeedopulseperkm()}`;
};

export const describeRequest = (request: Request): string => {
    switch (true) {
        case request.hasErase():
            return 'Erase flash';
        case request.hasWrite():
            return 'Write flash';
        case request.hasGetconfig():
            return 'Get config';
        case request.hasGetstatus():
            return 'Get status';
        case request.hasIdentify():
            return 'Identify';
        case request.hasSetconfig():
            return 'Set config';
        case request.hasSettestmode():
            return 'Set test mode';
        default:
            return 'Unknown';
    }
};

export const describeResponse = (response: Response): string => {
    switch (true) {
        case response.hasAck():
            return 'Ack';
        case response.hasNak():
            const str = new TextDecoder().decode(response.getNak()!.getErrormessage_asU8());
            if(str.length > 0) {
                return `Nak: ${str}`;
            } else {
                return 'Nak';
            }
        case response.hasIdent():
            const ident = response.getIdent()!;
            return `Ident: isBootloader ${ident.getIsbootloader()} version ${ident.getVersion()} hardware revision ${ident.getHardwarerevision()} base addr 0x${ident.getAppstartaddress().toString(16)} app size 0x${ident.getAppsize().toString(16)}`;
        case response.hasConfiguration():
            return describeConfiguration(response.getConfiguration()!);
        case response.hasStatus():
            return `Status response`;
        default:
            return 'Unknown';
    }
};
